import { splitHighlight } from "@/shared/lib/splitHighlight";
import { cn } from "@/shared/lib/cn";

export interface PageHeroTitleProps {
  /** El titular completo, ya traducido. */
  children: string;
  /**
   * La parte del titular que va resaltada. Debe aparecer literalmente
   * dentro de `children`; si no aparece, el titular se pinta entero sin
   * resaltar en vez de romperse. La regla vive en `splitHighlight`.
   */
  highlight?: string;
  className?: string;
}

/**
 * El `<h1>` de una portada de página, con una parte resaltada.
 *
 * ── El resalte es COLOR PLANO, no un degradado de texto ──────────────
 * Antes la mitad resaltada de `/paquetes` iba con `bg-gradient-to-r …
 * bg-clip-text`: violeta a la izquierda, tinta a la derecha. Se retiró
 * con el resto de degradados decorativos (regla de Anahi, 26-ago) y
 * además tenía un problema medible propio — el contraste de un texto en
 * degradado cambia letra a letra, así que "pasa" o "no pasa" deja de ser
 * una sola cifra.
 *
 * En plano hay UNA cifra, medida sobre la captura de `/paquetes` a 1440:
 * `ds-violet-pop` sobre el crema real de la portada da **5.45 : 1** de
 * principio a fin del resalte. El resto del titular, en `ds-ink`, 15.2 : 1.
 * El mínimo para texto pequeño es 4.5 : 1 y este titular mide entre 34 y
 * 58 px —texto grande, umbral 3 : 1—, así que pasa por los dos criterios.
 *
 * ── El salto de línea del resalte no se deja al azar ─────────────────
 * `whitespace-nowrap` sobre el trozo resaltado: sin él la frase se parte
 * donde caiga y el resalte queda cortado por la mitad, con media palabra
 * violeta arriba y media abajo. Así baja ENTERO a la línea siguiente.
 */
export function PageHeroTitle({ children, highlight, className }: PageHeroTitleProps) {
  const { before, highlight: hit, after } = splitHighlight(children, highlight);

  return (
    <h1
      className={cn(
        "text-[34px] font-light leading-[1.05] tracking-tight text-ds-ink sm:text-ds-h1 lg:text-ds-display",
        className,
      )}
    >
      {before}
      {hit && (
        <span className="whitespace-nowrap font-semibold text-ds-violet-pop">{hit}</span>
      )}
      {after}
    </h1>
  );
}
